import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Image, Input, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, SaveOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { utilityBannerService } from '../../../../services/next';

const { Text } = Typography;

/**
 * Thứ tự hiển thị và "Tiêu đề mục" của MỘT vị trí — hai việc mà trang cms.next làm ngay trên
 * bảng (kéo thả + ô "Tiêu đề mục" phía trên). Thứ tự lưu như CMS cũ: place = 1, 2, 3… theo
 * đúng thứ tự trên danh sách, kể cả dòng đang chờ duyệt.
 */
const UtilityBannerOrderModal = ({ open, onClose, positions, initialPosition, onSaved }) => {
  const [position, setPosition] = useState(initialPosition);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [title, setTitle] = useState('');
  const [savingOrder, setSavingOrder] = useState(false);
  const [savingTitle, setSavingTitle] = useState(false);

  // Mỗi lần mở đều chọn lại vị trí do trang truyền vào (vị trí đang lọc, hoặc Trang chủ),
  // không giữ vị trí của lần mở trước.
  useEffect(() => {
    if (open) setPosition(initialPosition);
  }, [open, initialPosition]);

  const currentPosition = positions.find((p) => p.value === position);

  useEffect(() => {
    setTitle(currentPosition?.title || '');
  }, [currentPosition]);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['utilityBannerOrder', position],
    queryFn: () => utilityBannerService.getList({ page: 1, limit: 1000, position }),
    enabled: open && Boolean(position),
  });

  useEffect(() => {
    setRows(data?.data || []);
    setDirty(false);
  }, [data]);

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = rows.slice();
    [next[index], next[target]] = [next[target], next[index]];
    setRows(next);
    setDirty(true);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      const items = rows.map((r, i) => ({ id: r.id, place: i + 1 }));
      const res = await utilityBannerService.sort(items);
      if (res?.code === 200) {
        message.success('Đã cập nhật thứ tự');
        setDirty(false);
        refetch();
        onSaved();
      } else {
        message.error(res?.message || 'Cập nhật thứ tự thất bại');
      }
    } catch (error) {
      message.error('Lỗi khi lưu thứ tự');
    } finally {
      setSavingOrder(false);
    }
  };

  const saveTitle = async () => {
    setSavingTitle(true);
    try {
      const res = await utilityBannerService.updateTitle(position, title);
      if (res?.code === 200) {
        message.success('Cập nhật tiêu đề mục thành công');
        onSaved();
      } else {
        message.error(res?.message || 'Cập nhật tiêu đề mục thất bại');
      }
    } catch (error) {
      message.error('Lỗi khi lưu tiêu đề mục');
    } finally {
      setSavingTitle(false);
    }
  };

  const columns = [
    { title: '#', key: 'index', width: 50, render: (_, __, i) => i + 1 },
    { title: 'Ảnh', dataIndex: 'icon', key: 'icon', width: 70, render: (img) => (img ? <Image src={img} width={36} height={36} /> : '-') },
    { title: 'Tên tiện ích', dataIndex: 'name', key: 'name', render: (t) => <Text strong>{t}</Text> },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s) => (s === 1 ? <Tag color="green">Đã duyệt</Tag> : <Tag color="orange">Chờ duyệt</Tag>),
    },
    {
      title: '',
      key: 'move',
      width: 90,
      align: 'right',
      render: (_, __, i) => (
        <Space size={4}>
          <Button size="small" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => move(i, -1)} />
          <Button size="small" icon={<ArrowDownOutlined />} disabled={i === rows.length - 1} onClick={() => move(i, 1)} />
        </Space>
      ),
    },
  ];

  const handleChangePosition = (value) => {
    if (dirty) {
      Modal.confirm({
        title: 'Bỏ thứ tự chưa lưu?',
        content: 'Thứ tự vừa sắp xếp ở vị trí hiện tại chưa được lưu.',
        okText: 'Bỏ',
        cancelText: 'Ở lại',
        onOk: () => setPosition(value),
      });
      return;
    }
    setPosition(value);
  };

  return (
    <Modal
      title="THỨ TỰ & TIÊU ĐỀ MỤC"
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      centered
      destroyOnClose
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Space wrap>
          <Text>Vị trí hiển thị</Text>
          <Select style={{ width: 280 }} value={position} options={positions} onChange={handleChangePosition} />
        </Space>

        <Space.Compact style={{ width: '100%' }}>
          <Input addonBefore="Tiêu đề mục" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tiêu đề mục" />
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={savingTitle}
            disabled={!currentPosition || title === (currentPosition?.title || '')}
            onClick={saveTitle}
          >
            Lưu tiêu đề
          </Button>
        </Space.Compact>

        <Alert
          type="info"
          showIcon
          message="Dùng mũi tên để đổi thứ tự rồi bấm Lưu thứ tự. Đã duyệt luôn đứng trước Chờ duyệt trên danh sách."
        />

        <Table
          size="small"
          rowKey="id"
          loading={isFetching}
          dataSource={rows}
          columns={columns}
          pagination={false}
          scroll={{ y: 420 }}
        />

        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Đóng</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={savingOrder} disabled={!dirty} onClick={saveOrder}>
            Lưu thứ tự
          </Button>
        </Space>
      </Space>
    </Modal>
  );
};

UtilityBannerOrderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  positions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string, title: PropTypes.string })).isRequired,
  initialPosition: PropTypes.string,
  onSaved: PropTypes.func.isRequired,
};

UtilityBannerOrderModal.defaultProps = {
  initialPosition: undefined,
};

export default UtilityBannerOrderModal;
